import { Deduction } from "@/core/entities/deduction";
import { ApprovalRemittance } from "@/core/entities/deduction";
import { BrandMember } from "@/core/entities/lookup";
import { IDeductionColumns } from "@/core/repositories/deduction";
import {
  approvalsAtom,
  approvalRemittancesAtom,
  isLoadingAtom,
  totalCountAtom,
  currentPageAtom,
  pageLimitAtom,
  errorAtom,
  setIsLoadingAtom,
  setErrorAtom,
  setCurrentPageAtom,
  setTotalCountAtom,
  getApprovalsAtom,
  fetchApprovalsListAtom,
  columnsAtom,
  ApprovalFilterInfo,
  isFilterAppliedAtom,
  userHasClearedApprovalFiltersAtom,
  filterInfoAtom,
  setIsFilterAppliedAtom,
  clearFilterAtom,
  setFilterAtom,
  isRefreshingAtom,
  loggedInBrandMemberAtom,
  setLoggedInBrandMemberAtom,
  setSelectedApprovalsAtom,
  selectedApprovalsAtom,
  setIsApprovalModalOpenAtom,
  isApprovalModalOpenAtom,
  setSelectedApprovalAtom,
  selectedApprovalAtom,
  approvalActionAtom,
  ApprovalAction,
  setApprovalActionAtom,
  sendForApprovalBulkAtom,
  resetApprovalWorkflowAtom,
  groupByCheckAtom,
  setGroupByCheckAtom,
  expandedRemittanceKeyAtom,
  setExpandedRemittanceKeyAtom,
} from "@/store/approvals";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";

interface GetApprovalsParams {
  isLoading: boolean;
  isRefreshing: boolean;
}

interface UseApproval {
  approvals: Deduction[];
  approvalRemittances: ApprovalRemittance[];
  columns: IDeductionColumns[];
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  totalCount: number;
  currentPage: number;
  pageLimit: number;
  groupByCheck: boolean;
  expandedRemittanceKey: number | null;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;
  setTotalCount: (totalCount: number) => void;
  setCurrentPage: (page: number) => void;
  getApprovals: (params: GetApprovalsParams) => Promise<void>;
  fetchApprovalsList: (params: GetApprovalsParams) => Promise<void>;
  setGroupByCheck: (groupByCheck: boolean) => Promise<void>;
  setExpandedRemittanceKey: (remittanceId: number | null) => void;
  filterInfo: ApprovalFilterInfo;
  isFilterApplied: boolean;
  userHasClearedApprovalFilters: boolean;
  setFilter: (filterInfo: ApprovalFilterInfo) => void;
  clearFilter: () => void;
  setIsFilterApplied: (isFilterApplied: boolean) => void;
  loggedInBrandMember: BrandMember | null;
  setLoggedInBrandMember: (loggedInBrandMember: BrandMember) => void;
  selectedApprovals: Deduction[];
  setSelectedApprovals: (selectedApprovals: Deduction[]) => void;
  isApprovalModalOpen: boolean;
  setIsApprovalModalOpen: (isApprovalModalOpen: boolean) => void;
  selectedApproval: Deduction | null;
  setSelectedApproval: (selectedApproval: Deduction | null) => void;
  approvalAction: ApprovalAction;
  setApprovalAction: (action: ApprovalAction) => void;
  sendForApprovalBulk: (
    deductions: Deduction[],
    comment: string,
    approvalAction: "APPROVE" | "REJECT"
  ) => Promise<Deduction[] | undefined>;
  resetApprovalWorkflow: (deductionId: number) => Promise<boolean>;
}

export function useApproval(): UseApproval {
  const loggedInBrandMember = useAtomValue(loggedInBrandMemberAtom);
  const approvals = useAtomValue(approvalsAtom);
  const approvalRemittances = useAtomValue(approvalRemittancesAtom);
  const columns = useAtomValue(columnsAtom);
  const isLoading = useAtomValue(isLoadingAtom);
  const isRefreshing = useAtomValue(isRefreshingAtom);
  const error = useAtomValue(errorAtom);
  const totalCount = useAtomValue(totalCountAtom);
  const currentPage = useAtomValue(currentPageAtom);
  const pageLimit = useAtomValue(pageLimitAtom);
  const groupByCheck = useAtomValue(groupByCheckAtom);
  const expandedRemittanceKey = useAtomValue(expandedRemittanceKeyAtom);
  const filterInfo = useAtomValue(filterInfoAtom);
  const isFilterApplied = useAtomValue(isFilterAppliedAtom);
  const userHasClearedApprovalFilters = useAtomValue(userHasClearedApprovalFiltersAtom);
  const setFilter = useSetAtom(setFilterAtom);
  const clearFilter = useSetAtom(clearFilterAtom);
  const setIsFilterApplied = useSetAtom(setIsFilterAppliedAtom);
  const setLoggedInBrandMember = useSetAtom(setLoggedInBrandMemberAtom);
  const selectedApprovals = useAtomValue(selectedApprovalsAtom);
  const setSelectedApprovals = useSetAtom(setSelectedApprovalsAtom);
  const isApprovalModalOpen = useAtomValue(isApprovalModalOpenAtom);
  const setIsApprovalModalOpen = useSetAtom(setIsApprovalModalOpenAtom);
  const selectedApproval = useAtomValue(selectedApprovalAtom);
  const setSelectedApproval = useSetAtom(setSelectedApprovalAtom);
  const approvalAction = useAtomValue(approvalActionAtom);
  const setApprovalAction = useSetAtom(setApprovalActionAtom);
  const sendForApprovalBulk = useSetAtom(sendForApprovalBulkAtom);
  const resetApprovalWorkflow = useSetAtom(resetApprovalWorkflowAtom);

  const setIsLoading = useSetAtom(setIsLoadingAtom);
  const setError = useSetAtom(setErrorAtom);
  const setTotalCount = useSetAtom(setTotalCountAtom);
  const setCurrentPage = useSetAtom(setCurrentPageAtom);
  const getApprovalsAction = useSetAtom(getApprovalsAtom);
  const fetchApprovalsListAction = useSetAtom(fetchApprovalsListAtom);
  const setGroupByCheckAction = useSetAtom(setGroupByCheckAtom);
  const setExpandedRemittanceKeyAction = useSetAtom(setExpandedRemittanceKeyAtom);

  const getApprovals = useCallback(
    (params: GetApprovalsParams) => getApprovalsAction(params),
    [getApprovalsAction]
  );

  const fetchApprovalsList = useCallback(
    (params: GetApprovalsParams) => fetchApprovalsListAction(params),
    [fetchApprovalsListAction]
  );

  const setGroupByCheck = useCallback(
    (value: boolean) => setGroupByCheckAction(value),
    [setGroupByCheckAction]
  );

  const setExpandedRemittanceKey = useCallback(
    (remittanceId: number | null) => setExpandedRemittanceKeyAction(remittanceId),
    [setExpandedRemittanceKeyAction]
  );

  return {
    approvals,
    approvalRemittances,
    columns,
    isLoading,
    isRefreshing,
    error,
    totalCount,
    currentPage,
    pageLimit,
    groupByCheck,
    expandedRemittanceKey,
    filterInfo,
    isFilterApplied,
    userHasClearedApprovalFilters,
    loggedInBrandMember,
    setFilter,
    clearFilter,
    setIsFilterApplied,
    setIsLoading,
    setError,
    setTotalCount,
    setCurrentPage,
    getApprovals,
    fetchApprovalsList,
    setGroupByCheck,
    setExpandedRemittanceKey,
    setLoggedInBrandMember,
    selectedApprovals,
    setSelectedApprovals,
    isApprovalModalOpen,
    setIsApprovalModalOpen,
    selectedApproval,
    setSelectedApproval,
    approvalAction,
    setApprovalAction,
    sendForApprovalBulk,
    resetApprovalWorkflow,
  };
}
